import { Capacitor } from '@capacitor/core';
import {
  IonButton,
  IonButtons,
  IonCol,
  IonGrid,
  IonInput,
  IonLabel,
  IonList,
  IonRow,
  IonSelect,
  IonSelectOption,
} from '@ionic/react';
import classNames from 'classnames';
import { chevronDownOutline } from 'ionicons/icons';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { PostalAddress } from '@/interfaces/CustomerInterface/CustomerInterface';
import { ManagedModalRoutes } from '@/interfaces/ManagedModalInterface/ManagedModalInterface';
import { OcrNfcRequest } from '@/interfaces/OcrInterface/OcrInterface';
import { VersionControl } from '@/interfaces/VersionControlInterface/VersionControlInterface';
import { KeyModel } from '@/redux/eDniModal/appConfig/appConfig';
import {
  getCitiesByPostalCode,
  getStreetTypes,
} from '@/redux/eDniModal/eDniModalAPI';
import { PostOrder } from '@/redux/orderManaged/orderManagedInterface';
import { requestVersionControl } from '@/services/api/version';
import InsightService from '@/services/insights';
import { RootState } from '@/store';
import { validDNI, validNIE } from '@/utils/dniHelper';
import { blockCharacters } from '@/utils/formHelper';
import { buildDataOrderInvoice, buildPostOrder } from '@/utils/orderHelper';
import { VALIDATOR_PATTERN } from '@/utils/validators';

import AddressData from "./AddressData/AddressData";
import styles from "./OrderPersonalData.module.scss";

interface PersonalDataProps {
  sendIFrameUrl: (arg0: string) => void;
  nextStep: (arg0: ManagedModalRoutes, orderInvoiceData?: PostOrder) => void;
  handleLoading: (arg0: boolean) => void;
  isDocument?: boolean;
  personalDataOcrData?: OcrNfcRequest;
}

export default function PersonalData({
  nextStep,
  handleLoading,
  isDocument,
  personalDataOcrData,
}: PersonalDataProps) {
  const [nameValue, setNameValue] = useState<string>('');
  const [surnameValue, setSurnameValue] = useState<string>('');
  const [nameError, setNameError] = useState<string>('');
  const [surnameError, setSurnameError] = useState<string>('');
  const [identityError, setIdentityError] = useState<string>('');
  const [identity, setIdentity] = useState<string>('');
  const orderData = useSelector(
    (state: RootState) => state.orderData.data?.data,
  );
  const invoiceData = useSelector(
    (state: RootState) => state.invoiceManaged.data?.data,
  );
  const [initialAddressNfc, setInitialAddressNfc] = useState<string>('');
  const [errorAddress, setErrorAddress] = useState<boolean>(false);
  const processTypeData = useSelector(
    (state: RootState) => state.configSlice.versionControl?.eco_process_type_app,
  );

  // retomación
  // const retakenECO = sessionStorage.getItem('retaken-eco') || false;
  // const operationData = useSelector((state: any) => state.operation.data);

  useEffect(() => {
    handleLoading(false);
    if (personalDataOcrData && !isDocument) {
      InsightService.logTrace(
        '1. MANAGED - carga datos personales desde NFC - order',
        [
          { isDocument },
          { personalDataOcrData: personalDataOcrData.ocr_id },
          { direccion: personalDataOcrData.address },
        ],
      );
      // datos del OCR
      setNameValue(personalDataOcrData.name || '');
      setSurnameValue(personalDataOcrData.last_name || '');
      setIdentity(personalDataOcrData.document_id || '');
      setInitialAddressNfc(personalDataOcrData.address || 'UNKNOWN');
      changeNameInput(personalDataOcrData.name || '');
      changeSurnameInput(personalDataOcrData.last_name || '');
      changeIdentityInput(personalDataOcrData.document_id || '');
    } else if (!isDocument && orderData) {
      InsightService.logTrace(
        '1. MANAGED - carga datos personales desde pedido - no ocr',
        [{ isDocument }, { orderData: orderData.order_id }],
      );
      setNameValue(orderData.applicant.name || '');
      setSurnameValue(orderData.applicant.last_name || '');
      setIdentity(orderData.applicant.document_id || '');
      changeNameInput(orderData.applicant.name || '');
      changeSurnameInput(orderData.applicant.last_name || '');
      changeIdentityInput(orderData.applicant.document_id || '');
    }
  }, [isDocument, orderData]);

  useEffect(() => {
    handleLoading(false);
    if (personalDataOcrData && isDocument) {
      InsightService.logTrace(
        '1. MANAGED - carga datos personales desde NFC - presupuesto',
        [
          { isDocument },
          { personalDataOcrData: personalDataOcrData.ocr_id },
          { direccion: personalDataOcrData.address },
        ],
      );
      // datos del OCR
      setNameValue(personalDataOcrData.name || '');
      setSurnameValue(personalDataOcrData.last_name || '');
      setIdentity(personalDataOcrData.document_id || '');
      changeNameInput(personalDataOcrData.name || '');
      setInitialAddressNfc(personalDataOcrData.address || 'UNKNOWN');
      changeSurnameInput(personalDataOcrData.last_name || '');
      changeIdentityInput(personalDataOcrData.document_id || '');
    } else if (isDocument && invoiceData) {
      InsightService.logTrace(
        '1. MANAGED - carga datos personales desde presupuesto',
        [
          { isDocument },
          { invoiceData: invoiceData.third_party_response.invoice_id },
        ],
      );
      setNameValue(invoiceData.third_party_response.applicant.name || '');
      setSurnameValue(
        invoiceData.third_party_response.applicant.last_name || '',
      );
      setIdentity(invoiceData.third_party_response.applicant.document_id || '');
      changeNameInput(invoiceData.third_party_response.applicant.name || '');
      changeSurnameInput(
        invoiceData.third_party_response.applicant.last_name || '',
      );
      changeIdentityInput(
        invoiceData.third_party_response.applicant.document_id || '',
      );
    }
  }, [isDocument, invoiceData]);


  const handleContinue = async () => {
    InsightService.logTrace('2. MANAGED - continuar con datos personales', [
      { EsPresupuesto: isDocument },
      { addressData },
    ]);
    const parts = surnameValue.split(' ');
    for (let i = 0; i < parts.length; i++) {
      if (parts[i].length === 1) {
        setSurnameError('Indica un apellido válido');
        return;
      }
      setSurnameError('');
    }
    try {
      if (!isDocument) {
        if (!orderData) return;
      } else if (!invoiceData) return;

      handleLoading(true);
      if (invoiceData || orderData) {
        InsightService.logTrace(
          '3. MANAGED - ir a formateo de datos personales',
        );
        const dataOrderInvoice = buildDataOrderInvoice(
          orderData!,
          invoiceData!,
          {
            isDocument: isDocument || false,
            processType: 
            isDocument && processTypeData,
          },
        );
        if (!dataOrderInvoice) return;
        InsightService.logTrace('4. MANAGED - formateo de order');
        // comprueba que no esté ya añadido el número en la dirección para no añadirlo dos veces
        let addressSend: PostalAddress = {
          ...addressData,
        };
        if (
          addressData.number
          && !addressData.address.includes(addressData.number)
        ) {
          addressSend = {
            ...addressData,
            address: `${addressData.address} ${addressData.number}`,
          };
        }

        const postOrder = buildPostOrder({
          dataOrderInvoice,
          nameValue,
          surnameValue,
          identity,
          addressData: addressSend,
        });
        InsightService.logTrace('5. MANAGED - navegar a opciones de envío', [
          { postOrder },
        ]);
        nextStep(ManagedModalRoutes.SHIPPING_CLIENT_MANAGED, postOrder);
      }
    } catch (error) {
      InsightService.logTrace('Error al continuar con los datos personales', [
        { error },
      ]);
      console.error('Error:', error);
      handleLoading(false);
      nextStep(ManagedModalRoutes.UNKNOWN_ERROR);
    }
  };

  // direccion
  const [manualMode, setManualMode] = useState<boolean>(false);
  const [citiesByPostalCodeList, setCitiesByPostalCodeList] = useState<
  KeyModel[]
  >([]);
  const [streetTypeList, setStreetTypeList] = useState<KeyModel[]>([]);
  const [postalCodeError, setPostalCodeError] = useState<string>('');
  const [addressData, setAddressData] = useState<PostalAddress>({
    address: '',
    city: '',
    number: '',
    postal_code: '',
    road_type: '',
    province: '',
    aux: '',
  });
  const [initialAddress, setInitialAddress] = useState('');

  const validatePostalCode = async (
    value: string,
    isManual: boolean = false,
  ) => {
    const postalCodePattern = /^\d{0,5}$/;

    if (!value) {
      setPostalCodeError('Indica el código postal');
      setAddressData((prevState) => ({
        ...prevState,
        postal_code: '',
      }));
      return;
    }

    if (!postalCodePattern.test(value)) {
      setPostalCodeError('Código postal inválido');
      setAddressData((prevState) => ({
        ...prevState,
        postal_code: value,
      }));
      return;
    }

    if (value.length === 5) {
      try {
        const cities = await fetchCitiesByPostalCode(value);
        if (cities && cities.length > 0) {
          setAddressData((prevState) => ({
            ...prevState,
            city: cities[0].city,
            province: cities[0].province,
            postal_code: value,
          }));
          setPostalCodeError('');
        } else if (isManual) {
          setPostalCodeError('Código postal inválido');
        } else {
          setPostalCodeError('Código postal inválido');
        }
      } catch (error) {
        setPostalCodeError('Código postal inválido');
      }
    } else {
      setAddressData((prevState) => ({
        ...prevState,
        postal_code: value,
      }));
    }
  };

  const fetchStreetTypes = async () => {
    try {
      const streetType = await getStreetTypes();
      setStreetTypeList(streetType[0].keys);
    } catch (error) {
      nextStep(ManagedModalRoutes.UNKNOWN_ERROR);
      handleLoading(false);
    }
  };

  const fetchCitiesByPostalCode = async (
    postalCode = addressData.postal_code,
  ) => {
    try {
      if (postalCode !== '') {
        const cities = await getCitiesByPostalCode(postalCode);
        setCitiesByPostalCodeList(cities);
        return cities;
      }
      return [];
    } catch (error) {
      // nextStep(ManagedModalRoutes.UNKNOWN_ERROR);
      handleLoading(false);
    }
  };

  useEffect(() => {
    setInitialAddress(
      isDocument
        ? invoiceData?.third_party_response?.applicant?.billing_address
          ?.address || ''
        : orderData?.applicant?.billing_address?.address || '',
    );
    if (manualMode) {
      fetchStreetTypes();
      fetchCitiesByPostalCode();
    }
    setAddressData({
      address: initialAddress,
      city: '',
      number: '',
      postal_code: '',
      road_type: '',
      province: '',
      aux: '',
    });
    // }
  }, [manualMode]);

  const changeNameInput = (value: string) => {
    setNameValue(value);
    if (!value) {
      setNameError('Indica tu nombre');
    } else if (!VALIDATOR_PATTERN.NAME.test(value)) {
      setNameError('Revisa los caracteres de tu nombre');
    } else {
      setNameError('');
    }
  };

  const changeSurnameInput = (value: string) => {
    setSurnameValue(value);
    const regex = /^[a-zA-ZÀ-ÿ\s-]*$/;
    if (!value || value.trim() === '') {
      setSurnameError('Indica tus apellidos');
      return;
    }
    setSurnameError('');

    if (!regex.test(value)) {
      setSurnameError('No se permiten números ni caracteres especiales');
      return;
    }
    setSurnameError('');

    if (value.includes('  ')) {
      setSurnameError('Indica un apellido válido');
    } else {
      setSurnameError('');
    }
  };

  const isDisabled = (): boolean => {
    // Verifica si hay errores en el código postal
    const hasAddressErrors = Boolean(postalCodeError);

    // verifica addressData
    const hasAddressDataErrors = Boolean(
      addressData.address === ''
      || addressData.city === ''
      || addressData.postal_code === ''
      || addressData.number === '',
    );
    if (manualMode) {
      return hasAddressErrors || hasAddressDataErrors;
    }
    return hasAddressErrors || hasAddressDataErrors || errorAddress;
  };

  const changeIdentityInput = (value: string) => {
    if (
      !VALIDATOR_PATTERN.DNI.test(value)
      && !VALIDATOR_PATTERN.NIE.test(value)
      && value.length > 0
    ) {
      setIdentityError('Indica un DNI o NIE válido');
    } else if (VALIDATOR_PATTERN.NIE.test(value)) {
      if (!validNIE(value)) {
        setIdentityError('Indica un NIE válido');
      } else {
        setIdentityError('');
      }
    } else if (VALIDATOR_PATTERN.DNI.test(value)) {
      if (!validDNI(value)) {
        setIdentityError('Indica un DNI válido');
      } else {
        setIdentityError('');
      }
    } else {
      setIdentityError('');
      setIdentity(value);
    }
    setIdentity(value);
  };

  const localSearchInputDefaultValue = (): {
    value: string;
    valueNull: boolean;
  } => {
    if (personalDataOcrData) {
      if (initialAddressNfc === 'UNKNOWN') {
        return { value: '', valueNull: true };
      }
      return { value: initialAddressNfc, valueNull: false };
    }
    if (isDocument) {
      if (
        !invoiceData?.third_party_response?.applicant?.billing_address
          .province
        && !invoiceData?.third_party_response?.applicant?.billing_address.city
      ) {
        return { value: '', valueNull: true };
      }
      const invoiceAddress = invoiceData?.third_party_response?.applicant?.billing_address;
      const cityD = `- ${invoiceAddress?.city}` || '';
      const provinceD = ` - ${invoiceAddress?.province}` || '';
      return {
        value: `${invoiceAddress?.address} ${cityD} ${provinceD}` || '',
        valueNull: false,
      };
    }

    if (
      !orderData?.applicant?.billing_address.province
      && !orderData?.applicant?.billing_address.city
    ) {
      return { value: '', valueNull: true };
    }
    const orderAddress = orderData?.applicant?.billing_address;
    const cityD2 = ` - ${orderAddress?.city}` || '';
    const provinceD2 = ` - ${orderAddress?.province}` || '';
    return {
      value: `${orderAddress?.address} ${cityD2} ${provinceD2}` || '',
      valueNull: false,
    };
  };

  return (
    <div className={styles.modalPersonalDataContainer}>
      <div className={styles.orderPersonalDataForm}>
        <div className={styles.orderManagedTitleDescriptionWrapper}>
          <div className={styles.orderManagedTitleWrapper}>
            <IonLabel className={styles.modalOrderManagedTitle}>
              Confirmación de datos
            </IonLabel>
          </div>
          <div className={styles.orderManagedTitleWrapper}>
            <IonLabel className={styles.modalOrderManagedDescription}>
              Necesitamos que confirmes los datos del cliente:
            </IonLabel>
          </div>
        </div>
      </div>
      <div className={styles.orderPersonalDataForm}>
        <IonLabel className={styles.modalTitle}>Datos de contacto</IonLabel>
        <IonGrid className={styles.modalFormWrapper}>
          <IonRow className={styles.modalFormRow}>
            <IonCol>
              <IonInput
                type="text"
                label="Nombre"
                maxlength={30}
                value={nameValue}
                // errorText={nameError}
                labelPlacement="floating"
                className={classNames(
                  styles.systemFont,
                  styles.upperInput,
                  styles.inputFirstForm,
                  {
                    'ion-invalid': nameError,
                    'ion-valid': !nameError,
                  },
                )}
                onIonInput={(e: any) => {
                  changeNameInput(e.target.value);
                }}
                onIonBlur={(e: any) => changeNameInput(e.target.value)}
              />
              {nameError && (
                <span className={styles.validationError}>{nameError}</span>
              )}
            </IonCol>
          </IonRow>
          <IonRow className={styles.modalFormRow}>
            <IonCol>
              <IonInput
                type="text"
                label="Apellidos"
                value={surnameValue}
                // onKeyDown={handleKeyDown}
                //  errorText={surnameError}
                labelPlacement="floating"
                className={classNames(
                  'system-font',
                  styles.upperInput,
                  styles.inputCommonForm,
                  {
                    'ion-invalid': surnameError,
                    'ion-valid': !surnameError,
                  },
                )}
                onIonInput={(e: any) => {
                  changeSurnameInput(e.target.value);
                }}
                onIonBlur={(e: any) => changeSurnameInput(e.target.value)}
              />
              {surnameError && (
                <span className={styles.validationError}>{surnameError}</span>
              )}
            </IonCol>
          </IonRow>
          <IonRow className={styles.modalFormRow}>
            <IonCol>
              <IonInput
                type="text"
                label="DNI o NIE"
                value={identity}
                errorText={identityError}
                labelPlacement="floating"
                className={classNames(
                  styles.systemFont,
                  styles.upperInput,
                  styles.inputEndForm,
                  {
                    'ion-invalid': identityError,
                    'ion-valid': !identityError,
                  },
                )}
                onIonInput={(e: any) => {
                  changeIdentityInput(e.target.value);
                }}
                onIonBlur={(e: any) => changeIdentityInput(e.target.value)}
                maxlength={9}
              />
              {identityError && (
                <span className={styles.validationError}>{identityError}</span>
              )}
            </IonCol>
          </IonRow>
        </IonGrid>
      </div>
      {manualMode === false ? (
        <AddressData
          defaultValue={localSearchInputDefaultValue()}
          handleLoading={(data) => handleLoading(data)}
          setManualMode={(data) => setManualMode(data)}
          addressData={addressData}
          setErroresForm={(errorAddress: boolean) => setErrorAddress(errorAddress)}
          setAddressData={(data) => setAddressData(data)}
        />
      ) : (
        <div className={styles.laborDataForm}>
          <IonLabel className={styles.modalTitle}>Dirección</IonLabel>
          <IonGrid className={styles.modalFormWrapper}>
            <IonRow className={styles.modalFormRow}>
              <IonCol size="4">
                <IonList lines="full">
                  <IonSelect
                    label="Tipo de vía"
                    labelPlacement="stacked"
                    className={classNames(
                      styles.entryProductInput,
                      styles.upperLeftInput,
                    )}
                    value={addressData.road_type}
                    interface="popover"
                    onIonChange={(e: any) => setAddressData({
                      ...addressData,
                      road_type: e.target.value,
                    })}
                    toggleIcon={chevronDownOutline}
                  >
                    {streetTypeList.map((status: KeyModel) => (
                      <IonSelectOption
                        key={status.code}
                        className={styles.laborEntryProductOption}
                      >
                        {status.description_code}
                      </IonSelectOption>
                    ))}
                  </IonSelect>
                </IonList>
              </IonCol>
              <IonCol size="8">
                <IonInput
                  label="Dirección"
                  labelPlacement="stacked"
                  value={addressData.address}
                  className={styles.upperRightInput}
                  onIonInput={(e: any) => setAddressData({
                    ...addressData,
                    address: e.target.value,
                  })}
                />
              </IonCol>
            </IonRow>
            <IonRow className={styles.modalFormRow}>
              <IonCol size="4">
                <IonInput
                  label="Nº"
                  type="tel"
                  maxlength={5}
                  labelPlacement="stacked"
                  value={addressData.number}
                  onIonInput={(e: any) => setAddressData({
                    ...addressData,
                    number: e.target.value,
                  })}
                />
              </IonCol>

              <IonCol size="8">
                <IonInput
                  label="Escalera, Portal, Piso..."
                  labelPlacement="stacked"
                  value={addressData.aux}
                  onIonInput={(e: any) => setAddressData({
                    ...addressData,
                    aux: e.target.value,
                  })}
                />
              </IonCol>
            </IonRow>

            <IonRow className={styles.modalFormRow}>
              <IonCol>
                <IonInput
                  label="C.P."
                  type="tel"
                  maxlength={5}
                  pattern="[0-9]*"
                  labelPlacement="stacked"
                  value={addressData.postal_code}
                  className={classNames(styles.inputCommonForm, {
                    'ion-invalid ion-touched': postalCodeError,
                    'ion-valid': !postalCodeError,
                  })}
                  // errorText={postalCodeError}
                  onIonInput={(e: any) => validatePostalCode(e.target.value, true)}
                  onKeyDown={blockCharacters}
                />
                {postalCodeError && (
                  <span className={styles.validationError}>
                    {postalCodeError}
                  </span>
                )}
              </IonCol>
            </IonRow>

            <IonRow className={styles.modalFormRow}>
              <IonCol>
                <IonList lines="full">
                  <IonSelect
                    label="Población"
                    labelPlacement="stacked"
                    className={classNames(
                      styles.inputCommonForm,
                      styles.entryProductInput,
                    )}
                    value={addressData.city}
                    interface="popover"
                    onIonChange={(e: any) => setAddressData({
                      ...addressData,
                      city: e.target.value,
                    })}
                    toggleIcon={chevronDownOutline}
                  >
                    {citiesByPostalCodeList.map((status: any) => (
                      <IonSelectOption
                        key={status.city}
                        className={styles.entryProductOption}
                      >
                        {status.city}
                      </IonSelectOption>
                    ))}
                  </IonSelect>
                </IonList>
              </IonCol>
            </IonRow>
            <IonRow className={styles.modalFormRow}>
              <IonCol>
                <IonInput
                  label="Provincia"
                  labelPlacement="stacked"
                  className={classNames(styles.lowerInput)}
                  value={addressData.province}
                  disabled
                />
              </IonCol>
            </IonRow>
          </IonGrid>
        </div>
      )}
      <div className={styles.modalOrderManagedButtonsWrapper}>
        <IonButtons className={styles.nextBtnWrapper}>
          <IonButton
            className="nextButton"
            onClick={handleContinue}
            disabled={
              !nameValue
              || !surnameValue
              || !!nameError
              || !!surnameError
              || !!identityError
              || isDisabled()
            }
          >
            Continuar
          </IonButton>
        </IonButtons>
      </div>
    </div>
  );
}
